"use client";
import { useState, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

interface Leader {
  id: string;
  name: string;
  title: string;
  image?: string;
  role?: string;
  experience?: string;
}

const boardDirectors = [
  {
    name: "Betelhem Tesfaye",
    title: "Chairperson",
    image: "/images/about/bethelhem.jpg",
    role: "board",
  },
  {
    name: "Elias Amare",
    title: "Member",
    image: "/images/about/Elias-Amare.jpg",
    role: "board",
  },
  {
    name: "Nebiyou Teshome",
    title: "Member",
    image: "/images/about/nebyu.jpg",
    role: "board",
  },
];

const executiveManagement: Leader[] = [
  {
    id: "tekalign-birhanu",
    name: "Tekalign Birhanu",
    title: "CEO",
    image: "/images/about/pic2.png",
    role: "executive",
  },
  {
    id: "amanuel-mengesha",
    name: "Amanuel Mengesha",
    title: "Chief Operation Officer",
    image: "/images/about/aman.jpg",
    role: "executive",
  },
  {
    id: "ebisa-derbie",
    name: "Ebisa Deribie",
    title: "Chief Corporate Service Officer",
    // image: "/images/about/ebisa.jpg",
    image: "/images/about/image copy.png",

    role: "executive",
  },
  {
    id: "selam-dirshaye",
    name: "Selam Dirshaye",
    title: "Vice President-Finance",
    image: "/images/about/selam.jpg",
    role: "executive",
  },
  // {
  //   id: "theodros-tesfaye",
  //   name: "Theodros Tesfaye",
  //   title: "Strategy Advisor to the CEO",
  //   image: "/images/about/theodross.jpg",
  //   role: "executive",
  // },
  {
    id: "ezra-tilahun",
    name: "Ezra Tilahun(PhD)",
    title: "Executive Director,Partnership & GNGO Relationship Directorate",
    image: "/images/about/ezrau.jpg",
    role: "executive",
  },
  {
    id: "habtemariam-adere",
    name: "Habtemariam Adere",
    title: "Executive Director-Strategy & Change Management",
    image: "/images/about/Habtemariam.jpg",
    role: "executive",
  },
];

const BoardMemberCard = ({ member }) => (
  <motion.div
    className="group relative h-64 w-full overflow-hidden rounded-2xl shadow-lg"
    whileHover={{ scale: 1.02 }}
  >
    <div className="relative h-full w-full">
      <Image
        src={member.image}
        alt={member.name}
        fill
        className="object-cover object-top grayscale transition-all duration-300 group-hover:grayscale-0"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <div className="absolute bottom-0 w-full p-4 text-center">
        <h3 className="font-orbitron text-lg text-white">{member.name}</h3>
        <p className="font-poppins text-sm text-gray-200">{member.title}</p>
      </div>
    </div>
  </motion.div>
);

const ExecutiveCard = ({ leader, isCEO = false }: { leader: Leader; isCEO?: boolean }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const onToggle = () => setIsExpanded((s) => !s);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <motion.div
      className={`group relative w-full overflow-hidden bg-white shadow-md transition-all duration-300 ${
        isCEO 
          ? "border rounded-md" 
          : "border border-gray-100 hover:shadow-lg rounded-md"
      }`}
      style={{ borderColor: isCEO ? colors.primary : undefined }}
      whileHover={{ scale: isCEO ? 1.01 : 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div
        role="button"
        aria-pressed={isExpanded}
        aria-expanded={isExpanded}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={`relative cursor-pointer ${isCEO ? "h-64" : "h-56"}`}
        onClick={onToggle}
      >
        {leader.image ? (
          <div className="absolute inset-0 flex items-center justify-center p-3">
            <div className={`relative ${isCEO ? "h-52 w-52" : "h-44 w-44"}`}>
              <Image
                src={leader.image}
                alt={leader.name}
                fill
                className="object-cover object-top rounded-sm"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                priority={isCEO}
              />
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center p-3" style={{ background: `linear-gradient(to bottom right, ${colors.primary}05, ${colors.primaryLight}05)` }}>
            <div className={`flex items-center justify-center rounded-full text-white font-orbitron ${
              isCEO ? "h-32 w-32 text-3xl" : "h-28 w-28 text-2xl"
            }`} style={{ backgroundColor: colors.primary }}>
              {(leader.name && leader.name.charAt(0)) ?? "?"}
            </div>
          </div>
        )}
        
        {isCEO && (
          <div className="absolute top-2 left-2 text-white px-2 py-1 rounded text-xs font-poppins font-semibold" style={{ backgroundColor: colors.primary }}>
            CEO
          </div>
        )}
      </div>

      <div className="p-3 text-center">
        <h3 className={`font-orbitron font-semibold mb-1 ${
          isCEO ? "text-lg" : "text-base"
        }`} style={{ color: colors.primary }}>
          {leader.name}
        </h3>
        <p className="font-poppins leading-tight text-xs" style={{ color: colors.textDark }}>
          {leader.title}
        </p>
      </div>

      <AnimatePresence>
        {isExpanded && leader.experience && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-100"
            style={{ backgroundColor: `${colors.primary}05` }}
          >
            <div className="p-2">
              <p className="font-poppins text-xs italic text-center leading-relaxed" style={{ color: colors.textDark }}>
                {leader.experience}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Leadership = () => {
  return (
    <div style={{ backgroundColor: colors.white }}>
      <section className="py-8 sm:py-16" style={{ background: `linear-gradient(to bottom, ${colors.primary}03, ${colors.white}, ${colors.primary}03)` }}>
        <div className="container mx-auto px-4 sm:px-6">
          {/* Header */}
          <motion.div
            className="text-center mb-8 sm:mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4" style={{ color: colors.primary }}>
              Executive Management Team
            </h2>
          </motion.div>

          {/* CEO Highlight Card - FIXED RESPONSIVE VERSION */}
          <motion.div
            className="flex justify-center mb-12 lg:mb-16 px-0 sm:px-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative group w-full max-w-3xl">
              <div className="absolute -inset-4 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(to right, ${colors.primary}10, ${colors.primaryLight}10)` }}></div>
              
              <div className="relative rounded-xl shadow-xl border overflow-hidden" style={{ backgroundColor: colors.white, borderColor: `${colors.primary}10` }}>
                {/* Mobile: Column layout, Desktop: Row layout */}
                <div className="flex flex-col md:flex-row items-center md:items-stretch">
                  
                  {/* Image Section - Fixed for mobile */}
                  <div className="w-full md:w-2/5 p-4 sm:p-6 md:p-8">
                    <div className="relative w-full max-w-[380px] mx-auto md:max-w-full aspect-square rounded-lg overflow-hidden border-4 shadow-lg" style={{ borderColor: colors.white }}>
                      {executiveManagement[0].image ? (
                        <>
                          <Image
                            src={executiveManagement[0].image}
                            alt={executiveManagement[0].name}
                            fill
                            className="object-cover object-top"
                            sizes="(max-width: 768px) 380px, (max-width: 1024px) 35vw, 30vw"
                            priority
                          />
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, ${colors.primary}, ${colors.primaryLight})` }}>
                          <span className="text-4xl sm:text-5xl text-white font-bold">
                            {executiveManagement[0].name.charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="w-full md:w-3/5 p-5 sm:p-6 md:p-8 text-center md:text-left">
                    <div className="mb-4">
                      <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 break-words" style={{ color: colors.primary }}>
                        {executiveManagement[0].name}
                      </h3>
                      <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
                        <div className="w-12 h-0.5" style={{ backgroundColor: colors.primaryLight }}></div>
                        <p className="text-base sm:text-lg md:text-xl font-poppins" style={{ color: colors.textDark }}>
                          {executiveManagement[0].title}
                        </p>
                      </div>
                    </div>
                    
                    {executiveManagement[0].experience && (
                      <div className="mt-4 sm:mt-6 p-3 sm:p-4 rounded-lg border-l-4 text-left" style={{ backgroundColor: `${colors.primary}05`, borderLeftColor: colors.primaryLight }}>
                        <p className="font-poppins text-sm sm:text-base italic" style={{ color: colors.textDark }}>
                          "{executiveManagement[0].experience}"
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Executive Team Grid */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto">
              {executiveManagement.slice(1).map((leader, index) => (
                <motion.div
                  key={leader.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                >
                  <div className="group relative h-full rounded-xl shadow-md border hover:shadow-xl transition-all duration-300 overflow-hidden" style={{ backgroundColor: colors.white, borderColor: `${colors.primary}10` }}>
                    <div className="absolute inset-0 transition-opacity duration-300 opacity-0 group-hover:opacity-100" style={{ background: `linear-gradient(to bottom right, ${colors.white}, ${colors.primary}05, ${colors.white})` }}></div>
                    
                    {/* Image container - responsive sizing (INCREASED CIRCLE SIZE) */}
                    <div className="relative pt-6 sm:pt-8 px-4 sm:px-8">
                      <div className="relative w-44 h-44 sm:w-52 sm:h-52 mx-auto rounded-full border-4 shadow-xl overflow-hidden group-hover:scale-105 transition-transform duration-300" style={{ borderColor: colors.white }}>
                        {leader.image ? (
                          <Image
                            src={leader.image}
                            alt={leader.name}
                            fill
                            className="object-cover object-top"
                            sizes="(max-width: 640px) 176px, (max-width: 1024px) 208px, 208px"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, ${colors.primary}, ${colors.primaryLight})` }}>
                            <span className="text-2xl sm:text-3xl text-white font-bold">
                              {leader.name.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      <div className="absolute top-4 right-4 flex gap-1">
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: `${colors.primaryLight}60` }}></div>
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: `${colors.primary}60` }}></div>
                        <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full" style={{ backgroundColor: `${colors.primaryLight}60` }}></div>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6 text-center">
                      <h4 className="text-base sm:text-lg md:text-xl font-bold mb-2 break-words" style={{ color: colors.primary }}>
                        {leader.name}
                      </h4>
                      <p className="font-poppins text-xs sm:text-sm md:text-base mb-4 leading-relaxed" style={{ color: colors.textDark }}>
                        {leader.title}
                      </p>
                      
                      {leader.experience && (
                        <div className="mt-4">
                          <div className="text-left p-3 sm:p-4 rounded-lg border" style={{ backgroundColor: `${colors.primary}03`, borderColor: `${colors.primary}10` }}>
                            <p className="font-poppins text-xs sm:text-sm leading-relaxed" style={{ color: colors.textDark }}>
                              {leader.experience}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(to right, transparent, ${colors.primaryLight}, transparent)` }}></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Leadership;